'use client'

/**
 * The NOT-YET preview, rendered under a NOT_YET answer on Home (#14086).
 *
 * The shape of the answer the asker would have got once the missing data is
 * connected — derived from the ledger by `@/lib/unmet-needs/not-yet-preview`,
 * never from the model — then ONE question: a multi-select over those
 * components, "which of these would be most useful to you?". The answer
 * lands on the turn's existing unmet-need record through
 * `saveUnmetNeedPreviewSelectionAction`; nothing here inserts.
 *
 * ## One question
 *
 * The group's `<legend>` IS the question, so assistive tech announces the
 * options as answers to it. The answer block above suppresses its own
 * NOT_YET question while this renders (`answer-class-block.tsx`), so the turn
 * asks one question on screen, not two.
 *
 * ## Plain text, keyboard-operable, 44px rows
 *
 * Every string comes from a closed vocabulary, so nothing is markdown. Each
 * option is a `Checkbox` paired with a `<Label htmlFor>` in a row that is
 * 44px tall on a touch screen (`pointer-coarse:min-h-11`) — the checkbox
 * primitive is 16px by contract and takes its pointer target from
 * its label (`@/components/ui/checkbox`). Space toggles, Tab walks, Enter
 * submits the form.
 *
 * ## Counted when it renders
 *
 * `ai_chat.not_yet_preview_shown` is reported from this component's mount
 * (`trackNotYetPreviewShownAction`), once per record, so the event counts
 * previews somebody saw rather than previews the server built for a frame
 * that may never have painted. Fire-and-forget: a failed report is
 * telemetry lost, never an error on screen.
 *
 * ## Where it lives today, first
 *
 * A missing domain whose metadata names a page the data can already be read
 * on (`learnMore`, #16820 — the SchoolAI Usage Report) leads the preview with
 * a link to it, opened in a new tab so the conversation stays put. Which
 * domains have one is the metadata's call, never this component's.
 *
 * Semantic tokens only, no `dark:` variants, no emoji; icons from
 * `@/components/icons`.
 */

import { useEffect, useId, useRef, useState, type FormEvent } from 'react'

import {
  saveUnmetNeedPreviewSelectionAction,
  trackNotYetPreviewShownAction,
} from '@/app/actions/unmet-needs'
import { ExternalLink, Eye } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import type { CapabilityIntent } from '@/lib/capability-ledger'
import type { NotYetPreview as NotYetPreviewModel } from '@/lib/unmet-needs/not-yet-preview'

/** What the status line says once the answer is stored. */
const NOT_YET_PREVIEW_THANKS =
  'Thanks — that tells us which data to connect first.'
/** What it says when the save failed without a message of its own. */
const NOT_YET_PREVIEW_SAVE_FAILED = 'Could not save your answer'

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; error: string }

export function NotYetPreview({
  unmetNeedId,
  preview,
}: {
  unmetNeedId: string
  preview: NotYetPreviewModel
}) {
  const baseId = useId()
  const [selected, setSelected] = useState<readonly CapabilityIntent[]>([])
  const [state, setState] = useState<SaveState>({ kind: 'idle' })

  const saving = state.kind === 'saving'

  // Once per record, not per render: a StrictMode re-run or a transcript
  // re-render of the same turn is not a second showing.
  const reportedFor = useRef<string | null>(null)
  useEffect(() => {
    if (reportedFor.current === unmetNeedId) return
    reportedFor.current = unmetNeedId
    trackNotYetPreviewShownAction({ unmetNeedId }).catch(() => {
      // Telemetry only; nothing for the asker to act on.
    })
  }, [unmetNeedId])

  function toggle(id: CapabilityIntent, checked: boolean) {
    setSelected(previous =>
      checked
        ? previous.includes(id)
          ? previous
          : [...previous, id]
        : previous.filter(value => value !== id),
    )
    if (state.kind === 'saved' || state.kind === 'failed') {
      setState({ kind: 'idle' })
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (selected.length === 0 || saving) return

    setState({ kind: 'saving' })
    try {
      const result = await saveUnmetNeedPreviewSelectionAction({
        unmetNeedId,
        components: selected,
      })
      setState(
        result.success
          ? { kind: 'saved' }
          : {
              kind: 'failed',
              error: result.error ?? NOT_YET_PREVIEW_SAVE_FAILED,
            },
      )
    } catch {
      // A rejected action must not strand the form in `saving` — see sub-issue R carve-out
      // (network failure or deploy skew; every input would stay disabled).
      setState({ kind: 'failed', error: NOT_YET_PREVIEW_SAVE_FAILED })
    }
  }

  return (
    <Card
      data-testid="not-yet-preview"
      className="mt-3 border-stroke-extra-light bg-surface-panel p-3 shadow-none"
    >
      <h4 className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
        <Eye className="h-3.5 w-3.5 shrink-0 text-text-secondary" aria-hidden />
        {preview.heading}
      </h4>
      {preview.learnMore.length > 0 && (
        <ul className="mt-1">
          {preview.learnMore.map(link => (
            <li key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="not-yet-preview-learn-more"
                className="inline-flex pointer-coarse:min-h-11 items-center gap-1 rounded-sm text-sm font-medium text-text-link hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected"
              >
                {link.label}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-sm text-text-secondary">{preview.lead}</p>

      <form className="mt-2" onSubmit={submit}>
        <fieldset>
          <legend className="text-sm font-medium text-text-primary">
            {preview.question}
          </legend>
          <ul className="mt-1">
            {preview.components.map(component => {
              const inputId = `${baseId}-${component.id}`
              return (
                <li
                  key={component.id}
                  data-testid="not-yet-preview-option"
                  className="flex pointer-coarse:min-h-11 items-center gap-2"
                >
                  <Checkbox
                    id={inputId}
                    checked={selected.includes(component.id)}
                    onCheckedChange={checked => toggle(component.id, checked)}
                    disabled={saving}
                  />
                  <Label htmlFor={inputId} className="text-text-secondary">
                    {component.label}
                  </Label>
                </li>
              )
            })}
          </ul>
        </fieldset>

        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            size="sm"
            className="pointer-coarse:min-h-11"
            disabled={selected.length === 0 || saving}
          >
            {saving ? 'Sending…' : 'Send'}
          </Button>
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-text-secondary"
          >
            {state.kind === 'saved'
              ? NOT_YET_PREVIEW_THANKS
              : state.kind === 'failed'
                ? state.error
                : ''}
          </p>
        </div>
      </form>
    </Card>
  )
}
